import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, functions, auth } from '../firebaseConfig';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

const SuperAdminPanel = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [signupRequests, setSignupRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [companyIdManualEdit, setCompanyIdManualEdit] = useState(false);
  const [companyIdExists, setCompanyIdExists] = useState(false);
  const [activeTab, setActiveTab] = useState('companies');

  // Billing state
  const [billingEditingCompany, setBillingEditingCompany] = useState(null);
  const [billingFormData, setBillingFormData] = useState({ billingAmount: '', billingEmail: '' });
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    companyId: '',
    adminEmail: '',
    maxDrivers: 4,
    trialDays: 14
  });

  // Security check - verify user is super admin
  useEffect(() => {
    const checkSuperAdmin = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          toast.error('Authentication required');
          navigate('/');
          return;
        }

        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          toast.error('User profile not found');
          navigate('/');
          return;
        }

        const userData = userDoc.data();

        // Check for superAdmin field
        if (userData.superAdmin !== true) {
          toast.error('Access denied: Super Admin privileges required');
          navigate('/map');
          return;
        }

        // User is authorized
        setAuthorized(true);
      } catch (error) {
        console.error('Error checking super admin status:', error);
        toast.error('Access verification failed');
        navigate('/');
      }
    };

    checkSuperAdmin();
  }, [navigate]);

  useEffect(() => {
    if (authorized) {
      loadData();
    }
  }, [authorized]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load companies
      const companiesSnapshot = await getDocs(
        query(collection(db, 'companies'), orderBy('__name__'))
      );
      const companiesList = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setCompanies(companiesList);

      // Load invitations
      const invitationsSnapshot = await getDocs(
        query(collection(db, 'invitations'), orderBy('createdAt', 'desc'))
      );
      const invitationsList = invitationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInvitations(invitationsList);

      // Load signup requests
      const signupRequestsSnapshot = await getDocs(
        query(collection(db, 'signupRequests'), orderBy('createdAt', 'desc'))
      );
      const signupRequestsList = signupRequestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSignupRequests(signupRequestsList);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateCompanyId = (companyName) => {
    // Generate company ID: 1 letter from each word, max 3 characters, uppercase only
    const words = companyName.trim().split(' ').filter(w => w.length > 0);
    let id = '';

    // Take first letter of each word (up to 3 words)
    for (let i = 0; i < Math.min(3, words.length); i++) {
      const firstLetter = words[i].charAt(0);
      if (firstLetter.match(/[a-zA-Z]/)) {
        id += firstLetter;
      }
    }

    return id.toUpperCase();
  };

  const checkCompanyIdExists = useCallback((companyId) => {
    if (!companyId || companyId.length === 0) {
      setCompanyIdExists(false);
      return;
    }

    // Check if companyId already exists in companies list
    const exists = companies.some(company =>
      company.companyId && company.companyId.toUpperCase() === companyId.toUpperCase()
    );
    setCompanyIdExists(exists);
  }, [companies]);

  // Re-check company ID availability when companies list or companyId changes
  useEffect(() => {
    if (formData.companyId) {
      checkCompanyIdExists(formData.companyId);
    }
  }, [companies, formData.companyId, checkCompanyIdExists]);

  const handleCompanyNameChange = (name) => {
    // Only auto-generate if not in manual edit mode
    if (!companyIdManualEdit) {
      const newId = generateCompanyId(name);
      setFormData({
        ...formData,
        companyName: name,
        companyId: newId
      });
      checkCompanyIdExists(newId);
    } else {
      setFormData({
        ...formData,
        companyName: name
      });
    }
  };

  const handleCompanyIdChange = (id) => {
    // Limit to 3 characters, uppercase, letters only
    const sanitized = id.toUpperCase().replace(/[^A-Z]/g, '').substring(0, 3);
    setFormData({
      ...formData,
      companyId: sanitized
    });
    checkCompanyIdExists(sanitized);
  };

  const toggleCompanyIdEdit = () => {
    setCompanyIdManualEdit(!companyIdManualEdit);
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.companyName || !formData.companyId || !formData.adminEmail) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (companyIdExists) {
      toast.error('Company ID already exists. Please choose a different one.');
      return;
    }

    if (formData.companyId.length < 2 || formData.companyId.length > 3) {
      toast.error('Company ID must be 2-3 characters');
      return;
    }

    if (formData.maxDrivers < 1) {
      toast.error('Max drivers must be at least 1');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.adminEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      toast.loading('Creating company and sending invitation...');

      // Create company document with companyId as document ID
      await setDoc(doc(db, 'companies', formData.companyId), {
        companyName: formData.companyName,
        companyId: formData.companyId,
        maxDrivers: parseInt(formData.maxDrivers),
        trialDays: parseInt(formData.trialDays),
        trialStartDate: null, // Set when admin completes setup
        createdAt: serverTimestamp(),
        status: 'trial-pending' // trial-pending, trial-active, active, suspended
      });

      // Generate unique invitation token
      const token = generateToken();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

      // Create invitation document
      await addDoc(collection(db, 'invitations'), {
        email: formData.adminEmail.toLowerCase().trim(),
        companyId: formData.companyId,
        companyName: formData.companyName,
        maxDrivers: parseInt(formData.maxDrivers),
        trialDays: parseInt(formData.trialDays),
        token: token,
        createdAt: serverTimestamp(),
        expiresAt: expiresAt,
        used: false,
        usedAt: null
      });

      // Send invitation email via Cloud Function
      const sendInvitation = httpsCallable(functions, 'sendInvitationEmail');
      await sendInvitation({
        email: formData.adminEmail,
        companyName: formData.companyName,
        token: token,
        trialDays: formData.trialDays,
        maxDrivers: formData.maxDrivers
      });

      toast.dismiss();
      toast.success(`Company created and invitation sent to ${formData.adminEmail}!`);

      // Reset form
      setFormData({
        companyName: '',
        companyId: '',
        adminEmail: '',
        maxDrivers: 4,
        trialDays: 14
      });
      setCompanyIdManualEdit(false);
      setCompanyIdExists(false);
      setShowCreateForm(false);

      // Reload data
      loadData();
    } catch (error) {
      toast.dismiss();
      console.error('Error creating company:', error);
      toast.error(`Failed to create company: ${error.message}`);
    }
  };

  const generateToken = () => {
    // Generate a secure random token
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const copyInvitationLink = (token) => {
    const link = `https://www.skipeye.com/setup?token=${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invitation link copied to clipboard!');
  };

  const handleApproveSignup = async (signup) => {
    // Pre-fill the form with signup data and switch to companies tab
    setFormData({
      companyName: signup.companyName,
      companyId: generateCompanyId(signup.companyName),
      adminEmail: signup.email,
      maxDrivers: signup.requestedDrivers,
      trialDays: 14
    });
    setShowCreateForm(true);
    setActiveTab('companies');
    toast.success('Form pre-filled with signup details. Review and create company.');

    // Mark signup as approved
    try {
      const signupRef = doc(db, 'signupRequests', signup.id);
      await updateDoc(signupRef, {
        status: 'approved',
        reviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid
      });
      // Reload data to refresh the list
      loadData();
    } catch (error) {
      console.error('Error updating signup status:', error);
    }
  };

  const handleRejectSignup = async (signup) => {
    const reason = prompt('Rejection reason (optional):');

    try {
      const signupRef = doc(db, 'signupRequests', signup.id);
      await updateDoc(signupRef, {
        status: 'rejected',
        reviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        rejectionReason: reason || null
      });

      toast.success('Signup request rejected');
      loadData();
    } catch (error) {
      console.error('Error rejecting signup:', error);
      toast.error('Failed to reject signup request');
    }
  };

  // Billing handlers
  const handleMarkAsPaid = async (companyId) => {
    if (!window.confirm('Mark this company as paid? This will also reactivate the account if suspended.')) return;
    setMarkingPaid(companyId);
    try {
      const markAsPaidFn = httpsCallable(functions, 'markAsPaid');
      await markAsPaidFn({ companyId });
      toast.success('Payment recorded successfully');
      loadData();
    } catch (error) {
      console.error('Error marking as paid:', error);
      toast.error(`Failed to mark as paid: ${error.message}`);
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleGenerateInvoice = async (companyId) => {
    setGeneratingInvoice(companyId);
    try {
      const generateInvoiceFn = httpsCallable(functions, 'generateInvoice');
      const result = await generateInvoiceFn({ companyId });
      const { pdf, fileName } = result.data;

      // Decode base64 and trigger download
      const byteCharacters = atob(pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error(`Failed to generate invoice: ${error.message}`);
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const handleToggleStatus = async (companyId, currentStatus) => {
    const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
    const action = newStatus === 'suspended' ? 'suspend' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} this company?`)) return;
    try {
      const companyRef = doc(db, 'companies', companyId);
      const updateData = { status: newStatus };
      if (newStatus === 'suspended') {
        updateData.billingStatus = 'suspended';
      }
      await updateDoc(companyRef, updateData);
      toast.success(`Company ${newStatus === 'suspended' ? 'suspended' : 'reactivated'} successfully`);
      loadData();
    } catch (error) {
      console.error('Error toggling company status:', error);
      toast.error(`Failed to ${action} company: ${error.message}`);
    }
  };

  const handleSaveBillingDetails = async (companyId) => {
    try {
      const amount = billingFormData.billingAmount === '' ? null : parseFloat(billingFormData.billingAmount);
      if (amount !== null && (isNaN(amount) || amount < 0)) {
        toast.error('Please enter a valid amount');
        return;
      }
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        billingAmount: amount,
        billingEmail: billingFormData.billingEmail || null
      });
      toast.success('Billing details saved');
      setBillingEditingCompany(null);
      loadData();
    } catch (error) {
      console.error('Error saving billing details:', error);
      toast.error(`Failed to save: ${error.message}`);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '2rem'
    },
    header: {
      maxWidth: '1400px',
      margin: '0 auto',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '0.5rem'
    },
    subtitle: {
      fontSize: '1rem',
      color: '#6b7280'
    },
    tabsContainer: {
      maxWidth: '1400px',
      margin: '0 auto 2rem auto',
      borderBottom: '2px solid #e5e7eb'
    },
    tabs: {
      display: 'flex',
      gap: '1rem'
    },
    tab: {
      padding: '0.75rem 1.5rem',
      fontSize: '1rem',
      fontWeight: '600',
      color: '#6b7280',
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: '3px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    tabActive: {
      color: '#667eea',
      borderBottomColor: '#667eea'
    },
    content: {
      maxWidth: '1400px',
      margin: '0 auto'
    },
    createButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#3B82F6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      marginBottom: '2rem'
    },
    formContainer: {
      backgroundColor: 'white',
      padding: '2rem',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      marginBottom: '2rem'
    },
    formTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '1.5rem'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
      marginBottom: '1rem'
    },
    formGroup: {
      marginBottom: '1.5rem'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      fontSize: '1rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      boxSizing: 'border-box'
    },
    formActions: {
      display: 'flex',
      gap: '1rem',
      marginTop: '1.5rem'
    },
    submitButton: {
      padding: '0.75rem 2rem',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer'
    },
    cancelButton: {
      padding: '0.75rem 2rem',
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer'
    },
    section: {
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: '1.5rem'
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1f2937'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '0.75rem',
      borderBottom: '2px solid #e5e7eb',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#6b7280'
    },
    td: {
      padding: '0.75rem',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '0.875rem'
    },
    badge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600'
    },
    badgePending: {
      backgroundColor: '#fef3c7',
      color: '#92400e'
    },
    badgeUsed: {
      backgroundColor: '#d1fae5',
      color: '#065f46'
    },
    badgeExpired: {
      backgroundColor: '#fee2e2',
      color: '#991b1b'
    },
    copyButton: {
      padding: '0.25rem 0.75rem',
      backgroundColor: '#3B82F6',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '0.75rem',
      cursor: 'pointer'
    },
    actionButtons: {
      display: 'flex',
      gap: '0.5rem'
    },
    approveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    rejectButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    emptyState: {
      textAlign: 'center',
      padding: '2rem',
      color: '#6b7280'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>Loading...</div>
      </div>
    );
  }

  // Show loading while checking authorization
  if (!authorized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#3B82F6'
      }}>
        Verifying access...
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Super Admin Panel</h1>
        <p style={styles.subtitle}>Manage companies and invitations</p>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabsContainer}>
        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'companies' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('companies')}
          >
            Companies
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'invitations' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('invitations')}
          >
            Invitations
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'signups' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('signups')}
          >
            Pending Signups
            {signupRequests.filter(s => s.status === 'pending').length > 0 && (
              <span style={{
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '700'
              }}>
                {signupRequests.filter(s => s.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'billing' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('billing')}
          >
            Billing
          </button>
        </div>
      </div>

      <div style={styles.content}>
        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <>
            {/* Create Company Button */}
            {!showCreateForm && (
              <button
                style={styles.createButton}
                onClick={() => setShowCreateForm(true)}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3B82F6'}
              >
                + Create New Company
              </button>
            )}

            {/* Create Company Form */}
            {showCreateForm && (
          <div style={styles.formContainer}>
            <h2 style={styles.formTitle}>Create New Company</h2>
            <form onSubmit={handleCreateCompany}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Company Name *</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="e.g., Alpha Waste Services"
                    value={formData.companyName}
                    onChange={(e) => handleCompanyNameChange(e.target.value)}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <label style={styles.label}>Company ID</label>
                    {!companyIdManualEdit && (
                      <span style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>(Auto-generated)</span>
                    )}
                    <button
                      type="button"
                      onClick={toggleCompanyIdEdit}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        backgroundColor: companyIdManualEdit ? '#10B981' : '#3B82F6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginLeft: 'auto'
                      }}
                      onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                      {companyIdManualEdit ? '✓ Editing' : '✎ Edit'}
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      style={{
                        ...styles.input,
                        backgroundColor: companyIdManualEdit ? 'white' : '#f3f4f6',
                        borderColor: companyIdExists ? '#EF4444' : (companyIdManualEdit ? '#3B82F6' : '#e5e7eb'),
                        borderWidth: '2px',
                        paddingRight: '80px'
                      }}
                      value={formData.companyId}
                      onChange={(e) => handleCompanyIdChange(e.target.value)}
                      required
                      maxLength={3}
                      placeholder="ABC"
                      disabled={!companyIdManualEdit}
                    />
                    <div style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {companyIdExists ? (
                        <span style={{ color: '#EF4444' }}>⚠ Exists</span>
                      ) : formData.companyId.length > 0 ? (
                        <span style={{ color: '#10B981' }}>✓ Available</span>
                      ) : null}
                    </div>
                  </div>
                  {companyIdExists && (
                    <div style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
                      This Company ID is already in use. Please choose a different one.
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    {companyIdManualEdit
                      ? '2-3 uppercase letters only'
                      : 'Takes first letter from each word (max 3)'}
                  </div>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Admin Email Address *</label>
                <input
                  type="email"
                  style={styles.input}
                  placeholder="john@alphawaste.co.uk"
                  value={formData.adminEmail}
                  onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                  required
                />
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Max Driver Accounts</label>
                  <input
                    type="number"
                    style={styles.input}
                    min="1"
                    value={formData.maxDrivers}
                    onChange={(e) => setFormData({ ...formData, maxDrivers: e.target.value })}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Trial Period (Days)</label>
                  <input
                    type="number"
                    style={styles.input}
                    min="0"
                    value={formData.trialDays}
                    onChange={(e) => setFormData({ ...formData, trialDays: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div style={styles.formActions}>
                <button
                  type="submit"
                  style={styles.submitButton}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                >
                  Create Company & Send Invitation
                </button>
                <button
                  type="button"
                  style={styles.cancelButton}
                  onClick={() => {
                    setShowCreateForm(false);
                    setFormData({
                      companyName: '',
                      companyId: '',
                      adminEmail: '',
                      maxDrivers: 4,
                      trialDays: 14
                    });
                    setCompanyIdManualEdit(false);
                    setCompanyIdExists(false);
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

            {/* Companies Table */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Companies</h2>
              {companies.length === 0 ? (
                <div style={styles.emptyState}>No companies yet</div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Company ID</th>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>Max Drivers</th>
                      <th style={styles.th}>Trial Days</th>
                      <th style={styles.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map(company => (
                      <tr key={company.id}>
                        <td style={styles.td}>
                          {company.companyId ? (
                            <strong>{company.companyId}</strong>
                          ) : (
                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not set</span>
                          )}
                        </td>
                        <td style={styles.td}>{company.companyName}</td>
                        <td style={styles.td}>{company.maxDrivers}</td>
                        <td style={styles.td}>{company.trialDays || 'N/A'}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.badge,
                            ...(company.status === 'trial-active' || company.status === 'active' ? styles.badgeUsed : styles.badgePending)
                          }}>
                            {company.status || 'unknown'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Invitations Tab */}
        {activeTab === 'invitations' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Recent Invitations</h2>
          {invitations.length === 0 ? (
            <div style={styles.emptyState}>No invitations yet</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(inv => {
                  const isExpired = inv.expiresAt && new Date(inv.expiresAt.toDate ? inv.expiresAt.toDate() : inv.expiresAt) < new Date();
                  const status = inv.used ? 'used' : (isExpired ? 'expired' : 'pending');

                  return (
                    <tr key={inv.id}>
                      <td style={styles.td}>{inv.companyName}</td>
                      <td style={styles.td}>{inv.email}</td>
                      <td style={styles.td}>
                        {inv.createdAt ? new Date(inv.createdAt.toDate ? inv.createdAt.toDate() : inv.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badge,
                          ...(status === 'pending' ? styles.badgePending : status === 'used' ? styles.badgeUsed : styles.badgeExpired)
                        }}>
                          {status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {status === 'pending' && (
                          <button
                            style={styles.copyButton}
                            onClick={() => copyInvitationLink(inv.token)}
                          >
                            Copy Link
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        )}

        {/* Pending Signups Tab */}
        {activeTab === 'signups' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Pending Signup Requests</h2>
            {signupRequests.length === 0 ? (
              <div style={styles.emptyState}>No signup requests yet</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Company Name</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Phone</th>
                    <th style={styles.th}>Drivers</th>
                    <th style={styles.th}>Source</th>
                    <th style={styles.th}>Submitted</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {signupRequests.map(signup => (
                    <tr key={signup.id}>
                      <td style={styles.td}>
                        <strong>{signup.companyName}</strong>
                        {signup.message && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                            {signup.message.substring(0, 50)}{signup.message.length > 50 ? '...' : ''}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>{signup.email}</td>
                      <td style={styles.td}>{signup.phone || '-'}</td>
                      <td style={styles.td}>{signup.requestedDrivers}</td>
                      <td style={styles.td}>{signup.hearAboutUs || '-'}</td>
                      <td style={styles.td}>
                        {signup.createdAt ? new Date(signup.createdAt.toDate ? signup.createdAt.toDate() : signup.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badge,
                          ...(signup.status === 'pending' ? styles.badgePending : signup.status === 'approved' ? styles.badgeUsed : styles.badgeExpired)
                        }}>
                          {signup.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {signup.status === 'pending' ? (
                          <div style={styles.actionButtons}>
                            <button
                              style={styles.approveButton}
                              onClick={() => handleApproveSignup(signup)}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                            >
                              Approve
                            </button>
                            <button
                              style={styles.rejectButton}
                              onClick={() => handleRejectSignup(signup)}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                            {signup.status === 'approved' ? 'Processed' : 'Rejected'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Billing Management</h2>
            {(() => {
              const billingCompanies = companies.filter(c => c.status !== 'trial-pending');
              if (billingCompanies.length === 0) {
                return <div style={styles.emptyState}>No active companies to bill</div>;
              }
              return (
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Company</th>
                        <th style={styles.th}>Monthly Amount</th>
                        <th style={styles.th}>Billing Email</th>
                        <th style={styles.th}>Billing Status</th>
                        <th style={styles.th}>Last Payment</th>
                        <th style={styles.th}>Trial / Status</th>
                        <th style={styles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingCompanies.map(company => {
                        const isEditing = billingEditingCompany === company.id;
                        const billingStatusColor = {
                          paid: { backgroundColor: '#d1fae5', color: '#065f46' },
                          due: { backgroundColor: '#fef3c7', color: '#92400e' },
                          overdue: { backgroundColor: '#fee2e2', color: '#991b1b' },
                          suspended: { backgroundColor: '#f3f4f6', color: '#6b7280' }
                        }[company.billingStatus] || { backgroundColor: '#f3f4f6', color: '#6b7280' };

                        // Calculate trial end for trial-active companies
                        let trialInfo = null;
                        if (company.status === 'trial-active' && company.trialStartDate) {
                          const trialStart = company.trialStartDate.toDate ? company.trialStartDate.toDate() : new Date(company.trialStartDate);
                          const trialEnd = new Date(trialStart);
                          trialEnd.setDate(trialEnd.getDate() + (company.trialDays || 14));
                          const daysLeft = Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24)));
                          trialInfo = `Trial: ${daysLeft}d left`;
                        }

                        return (
                          <tr key={company.id}>
                            <td style={styles.td}>
                              <strong>{company.companyName}</strong>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{company.companyId || company.id}</div>
                            </td>
                            <td style={styles.td}>
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  style={{ ...styles.input, width: '100px', padding: '0.4rem' }}
                                  value={billingFormData.billingAmount}
                                  onChange={(e) => setBillingFormData({ ...billingFormData, billingAmount: e.target.value })}
                                  placeholder="0.00"
                                />
                              ) : company.billingAmount ? (
                                <span>{'\u00A3'}{company.billingAmount.toFixed(2)}</span>
                              ) : (
                                <span style={{ color: '#ef4444', fontStyle: 'italic' }}>Not set</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {isEditing ? (
                                <input
                                  type="email"
                                  style={{ ...styles.input, width: '180px', padding: '0.4rem' }}
                                  value={billingFormData.billingEmail}
                                  onChange={(e) => setBillingFormData({ ...billingFormData, billingEmail: e.target.value })}
                                  placeholder="billing@company.com"
                                />
                              ) : company.billingEmail ? (
                                <span>{company.billingEmail}</span>
                              ) : (
                                <span style={{ color: '#ef4444', fontStyle: 'italic' }}>Not set</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {company.billingStatus ? (
                                <span style={{
                                  ...styles.badge,
                                  ...billingStatusColor
                                }}>
                                  {company.billingStatus}
                                </span>
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {company.lastPaymentDate ? (
                                <span style={{ fontSize: '0.875rem' }}>
                                  {(company.lastPaymentDate.toDate ? company.lastPaymentDate.toDate() : new Date(company.lastPaymentDate)).toLocaleDateString('en-GB')}
                                </span>
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Never</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {trialInfo ? (
                                <span style={{ ...styles.badge, backgroundColor: '#dbeafe', color: '#1e40af' }}>
                                  {trialInfo}
                                </span>
                              ) : (
                                <span style={{
                                  ...styles.badge,
                                  ...(company.status === 'active' ? styles.badgeUsed :
                                     company.status === 'suspended' ? styles.badgeExpired : styles.badgePending)
                                }}>
                                  {company.status || 'unknown'}
                                </span>
                              )}
                            </td>
                            <td style={styles.td}>
                              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                {isEditing ? (
                                  <>
                                    <button
                                      style={{ ...styles.approveButton, padding: '0.35rem 0.75rem', fontSize: '0.75rem' }}
                                      onClick={() => handleSaveBillingDetails(company.id)}
                                    >
                                      Save
                                    </button>
                                    <button
                                      style={{ ...styles.cancelButton, padding: '0.35rem 0.75rem', fontSize: '0.75rem' }}
                                      onClick={() => setBillingEditingCompany(null)}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      style={{ ...styles.copyButton, backgroundColor: '#6b7280' }}
                                      onClick={() => {
                                        setBillingEditingCompany(company.id);
                                        setBillingFormData({
                                          billingAmount: company.billingAmount || '',
                                          billingEmail: company.billingEmail || ''
                                        });
                                      }}
                                    >
                                      Edit Billing
                                    </button>
                                    {(company.billingStatus === 'due' || company.billingStatus === 'overdue' || company.billingStatus === 'suspended') && (
                                      <button
                                        style={{ ...styles.copyButton, backgroundColor: '#10b981' }}
                                        onClick={() => handleMarkAsPaid(company.id)}
                                        disabled={markingPaid === company.id}
                                      >
                                        {markingPaid === company.id ? 'Processing...' : 'Mark Paid'}
                                      </button>
                                    )}
                                    {company.billingAmount > 0 && (
                                      <button
                                        style={{ ...styles.copyButton, backgroundColor: '#667eea' }}
                                        onClick={() => handleGenerateInvoice(company.id)}
                                        disabled={generatingInvoice === company.id}
                                      >
                                        {generatingInvoice === company.id ? 'Generating...' : 'Invoice'}
                                      </button>
                                    )}
                                    {(company.status === 'active' || company.status === 'suspended') && (
                                      <button
                                        style={{
                                          ...styles.copyButton,
                                          backgroundColor: company.status === 'suspended' ? '#ef4444' : '#10b981'
                                        }}
                                        onClick={() => handleToggleStatus(company.id, company.status)}
                                      >
                                        {company.status === 'suspended' ? 'Suspended' : 'Active'}
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminPanel;
